interface Props {
  onApply: () => void;
}

const cards = [
  {
    id: 'event-placement',
    icon: 'ri-pushpin-2-fill',
    iconBg: 'bg-pink-100',
    iconColor: 'text-pink-600',
    name: 'Featured Event Placement',
    tagline: 'Be seen where attention is highest',
    price: '$75',
    priceNote: 'per event',
    priceColor: 'text-pink-600',
    border: 'border-pink-200',
    topBar: 'bg-gradient-to-r from-pink-400 to-rose-400',
    perks: [
      { icon: 'ri-pushpin-fill', text: 'Pinned to top of event page' },
      { icon: 'ri-megaphone-fill', text: 'Tagged in event promotions' },
      { icon: 'ri-article-fill', text: 'Included in recap content' },
    ],
    ctaLabel: 'Reserve a Placement',
    ctaClass: 'bg-pink-500 hover:bg-pink-600 text-white',
    badge: null,
  },
  {
    id: 'mama-approved',
    icon: 'ri-medal-fill',
    iconBg: 'bg-rose-100',
    iconColor: 'text-rose-600',
    name: 'Mama Approved',
    tagline: 'Trusted by local moms',
    price: 'Partner',
    priceNote: 'access only',
    priceColor: 'text-rose-600',
    border: 'border-rose-300',
    topBar: 'bg-gradient-to-r from-rose-400 to-pink-500',
    perks: [
      { icon: 'ri-book-open-fill', text: 'Displayed across guides + listings' },
      { icon: 'ri-shield-check-fill', text: 'Signals credibility instantly' },
      { icon: 'ri-lock-star-fill', text: 'Reserved for select partners only' },
    ],
    ctaLabel: 'Apply for Mama Approved',
    ctaClass: 'bg-rose-500 hover:bg-rose-600 text-white',
    badge: 'Most Trusted',
  },
  {
    id: 'priority-listing',
    icon: 'ri-arrow-up-circle-fill',
    iconBg: 'bg-gray-100',
    iconColor: 'text-gray-700',
    name: 'Priority Listing',
    tagline: 'Get seen before everyone else',
    price: '$25',
    priceNote: '/month',
    priceColor: 'text-gray-800',
    border: 'border-gray-200',
    topBar: 'bg-gradient-to-r from-gray-400 to-gray-500',
    perks: [
      { icon: 'ri-list-ordered', text: 'Shows above standard listings' },
      { icon: 'ri-compass-3-fill', text: 'Featured across guides + hubs' },
      { icon: 'ri-eye-fill', text: 'Ongoing visibility — not one-off' },
    ],
    ctaLabel: 'Get Priority Listing',
    ctaClass: 'bg-gray-900 hover:bg-black text-white',
    badge: null,
  },
];

export default function PartnerTiers({ onApply }: Props) {
  return (
    <section id="tiers" className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section header */}
        <div className="text-center mb-14">
          <span className="text-xs font-bold tracking-widest uppercase text-rose-500 mb-4 block">
            Partnership Options
          </span>
          <h2
            className="text-4xl font-extrabold text-gray-900 mb-4 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Partner With The Mommy Mingle
          </h2>
          <p className="text-gray-500 text-base max-w-lg mx-auto leading-relaxed">
            Get in front of{' '}
            <strong className="text-gray-800">3,000+ local moms</strong>{' '}
            who are actively looking for things to do, buy, and book.
          </p>
        </div>

        {/* 3-card grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          {cards.map((card) => (
            <div
              key={card.id}
              className={`relative bg-white rounded-2xl border-2 ${card.border} flex flex-col overflow-hidden`}
            >
              {/* Top accent bar */}
              <div className={`h-1.5 w-full ${card.topBar}`} />

              {/* Best value badge */}
              {card.badge && (
                <div className="absolute top-5 right-5">
                  <span className="bg-rose-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap">
                    {card.badge}
                  </span>
                </div>
              )}

              <div className="p-7 flex flex-col flex-1">
                {/* Icon + name */}
                <div className="flex items-start gap-3.5 mb-5">
                  <div className={`w-11 h-11 flex items-center justify-center ${card.iconBg} rounded-xl shrink-0`}>
                    <i className={`${card.icon} ${card.iconColor} text-xl`} />
                  </div>
                  <div>
                    <h3 className="text-base font-extrabold text-gray-900 leading-snug">{card.name}</h3>
                    <p className="text-xs text-gray-400 mt-0.5">{card.tagline}</p>
                  </div>
                </div>

                {/* Price */}
                <div className="mb-6 pb-5 border-b border-gray-100">
                  <span className={`text-3xl font-extrabold ${card.priceColor}`}>{card.price}</span>
                  <span className="text-xs text-gray-400 ml-1.5 font-medium">{card.priceNote}</span>
                </div>

                {/* Perks — 3 max */}
                <ul className="space-y-3.5 flex-1 mb-7">
                  {card.perks.map((perk) => (
                    <li key={perk.text} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                        <i className={`${perk.icon} text-rose-400 text-sm`} />
                      </div>
                      <span className="text-sm text-gray-700 leading-snug">{perk.text}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <button
                  onClick={onApply}
                  className={`w-full py-3 ${card.ctaClass} text-sm font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap`}
                >
                  {card.ctaLabel}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Footer note */}
        <div className="mt-12 text-center">
          <p className="text-gray-400 text-sm max-w-md mx-auto leading-relaxed">
            We keep this platform valuable for moms — which means we&apos;re selective about who we feature.
          </p>
          <p className="text-gray-400 text-xs mt-2.5">
            Not sure which option fits?{' '}
            <button
              onClick={onApply}
              className="text-rose-500 font-semibold hover:text-rose-600 transition-colors cursor-pointer"
            >
              Apply and we&apos;ll help you decide.
            </button>
          </p>
        </div>
      </div>
    </section>
  );
}
